import * as React from 'react';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { noop } from 'lodash';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation } from 'react-router-dom';
import TermAndConditionDialog from '../assessment/TermAndConditionDialog';
import UI from '../../constants/ui';
import useMobileDevice from '../../hooks/useMobileDevice';
import { getRequest, putRequest } from '../../services';
import {
  API_URL,
  getCandidateAssessment,
  getEvents,
} from '../../constants/apiUrls';
import { getFormattedDateTimeWOSecs } from '../../formatter/date';
import LOGIN_TYPE from '../../constants/loginType';
import FEEDBACK_SCHEMA from '../../components/footer/feedbackForm/feedBackForm.schema';
import COMMON_STYLE from '../../constants/commonStyle';
import { showSnackBar } from '../../../redux/snackBarSlice';
// import WinnersSlider from '../../components/winnerCandidates/winnersSlider';
import styles from './Events.module.css';

import EventRegistrationForm from './EventRegistrationForm';
import RegisterDialog from './RegisterDialog';
import { getLoginDetailFromSession } from '../../helpers/sessionDetails';
import { getAssessmentState, getSkill } from '../../formatter/commonBootstrap';
import AssessmentDetailsDialog from '../assessment/AssessmentDetailsDialog';
import ASSESSMENT_STATE from '../../constants/assessmentState';
import URL from '../../constants/urls';
import UpcomingEventsSection from './UpcomingEventsSection';

function Events({ role, setShowNavBar }) {
  const isMobileDevice = useMobileDevice();
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [termAndConditionDialogOpen, setTermAndConditionDialogOpen] =
    useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState();
  const [pastEvents, setPastEvents] = useState();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [eventDetailUrl, setEventDetailUrl] = useState();
  const [showInterestDialog, setShowInterestDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [eventName, setEventName] = useState(null);
  const [jobId, setJobId] = useState(null);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [statusLoadingIdId, setStatusLoadingIdId] = useState(null);
  const [pastLoading, setPastLoading] = useState(false);
  // const [showWinnersDetails, setShowWinnersDetails] = useState(false);
  const [eventID, setEventID] = useState();
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [assessmentIds, setAssessmentIds] = useState(null);
  const [interviewId, setInterviewId] = useState(null);
  const [interviewSkillIds, setInterviewSkillIds] = useState(null);
  const [technologies, setTechnologies] = useState([]);
  const [interviewSkills, setInterviewSkills] = useState([]);
  const [assessmentStatus, setAssessmentStatus] = useState({});
  const [showAssessmentDialog, setShowAssessmentDialog] = useState(false);
  const [currentAssessmentDetails, setCurrentAssessmentDetails] = useState({});
  const [assessmentCount, setAssessmentCount] = useState(0);

  const {
    register,
    handleSubmit: handleSubmitShowInterest,
    reset: resetShowInterest,
    watch,
    formState: { errors: showInterestErrors },
  } = useForm({
    resolver: yupResolver(FEEDBACK_SCHEMA),
    mode: 'onChange',
    defaultValues: {
      comments: `I am interested in ${eventName}...`,
    },
  });

  const loginDetails = getLoginDetailFromSession();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  useEffect(() => {
    const page = searchParams.get('page');
    if (page === 'upcoming') {
      setShowPastEvents(false);
      return;
    }
    if (page === 'past' || upcomingEvents?.length === 0) {
      setShowPastEvents(true);
    } else {
      setShowPastEvents(false);
    }
  }, [upcomingEvents]);

  useEffect(() => {
    setShowNavBar(true);
  }, [setShowNavBar]);

  // Fetch technologies options from the API
  useEffect(() => {
    const fetchTechnologies = () => {
      setLoading(true);
      try {
        getRequest(API_URL.GET_GLOBAL_TECHNOLOGIES)
          .then((res) => {
            const options = res.map((tech) => ({
              value: tech.techId,
              label: tech.dispName,
            }));
            setTechnologies(options);
          })
          .catch((err) => {
            dispatch(
              showSnackBar({
                setopen: true,
                message: err.msg,
                severity: 'error',
              })
            );
          });
      } catch (err) {
        showSnackBar({
          open: true,
          message: err?.msg,
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTechnologies();
  }, []);

  useEffect(() => {
    async function fetchTechSkill() {
      if (interviewSkillIds?.length > 0) {
        try {
          const skills = await Promise.all(
            interviewSkillIds.map((id) => getSkill(id))
          );
          setInterviewSkills(skills);
        } catch (err) {
          dispatch(
            showSnackBar({
              setopen: true,
              message: err.msg,
              severity: 'error',
            })
          );
        }
      }
    }
    fetchTechSkill();
  }, [interviewSkillIds]);

  useEffect(() => {
    if (upcomingEvents === undefined) {
      setLoading(true);
      getRequest(getEvents(true))
        .then((result) => {
          setUpcomingEvents(result);
        })
        .catch((error) => {
          dispatch(
            showSnackBar({
              setopen: true,
              message: error?.msg,
              severity: 'error',
            })
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }

    if (pastEvents === undefined && showPastEvents) {
      setPastLoading(true);
      getRequest(getEvents(false))
        .then((result) => {
          setPastEvents(result);
        })
        .catch((error) => {
          dispatch(
            showSnackBar({
              setopen: true,
              message: error?.msg,
              severity: 'error',
            })
          );
        })
        .finally(() => {
          setPastLoading(false);
        });
    }
  }, [showPastEvents]);

  useEffect(() => {
    resetShowInterest();
  }, [showInterestDialog]);

  const showExtraItems = () => {
    if (
      getLoginDetailFromSession() &&
      getLoginDetailFromSession()?.role === LOGIN_TYPE.CANDIDATE
    ) {
      return true;
    }
    return false;
  };

  const matchedTechnologies = React.useMemo(
    () =>
      assessmentIds?.length
        ? technologies.filter((option) => assessmentIds.includes(option.value))
        : [],
    [assessmentIds, technologies]
  );

  function handlePastEvents() {
    setShowPastEvents(!showPastEvents);
  }

  const handleDialogOpen = (url) => {
    setTermAndConditionDialogOpen(true);
    setEventDetailUrl(url);
  };
  // const handleOpenWinnersDetails = (id) => {
  //   setEventID(id);
  //   setShowWinnersDetails(!showWinnersDetails);
  // };
  const handleDialogClose = () => {
    setTermAndConditionDialogOpen(false);
    setEventDetailUrl();
    setInterviewSkillIds(null);
    setInterviewSkills(null);
    setInterviewId(null);
    setIsDialogOpen(false);
  };

  const isUnderDateRange = (startIso, endIso) => {
    const now = new Date();
    const start = new Date(startIso);
    const end = new Date(endIso);
    return now >= start && now <= end;
  };

  const handleClickRegister = async (row) => {
    setSelectedEventId(row.id);
    setStartDate(row.start);
    setEndDate(row.end);
    setEventName(row.event);
    setJobId(row.jobId);
    setCompanyName(row.companyName);
    setAssessmentIds(row.assessmentIds);
    setInterviewSkillIds(row.interviewSkillIds);
    setAssessmentCount(row.assessmentCount);
    setInterviewId(row.interviewId);
    if (row.registered) {
      if (isUnderDateRange(row.start, row.end)) {
        setStatusLoadingIdId(row.id);
        try {
          const res = await getRequest(`/event/${row.id}/status`);
          const ids = Object.keys(res).map((idStr) => Number(idStr));
          setAssessmentIds(ids);
          const statusMap = {};
          Object.entries(res).forEach(([k, v]) => {
            statusMap[Number(k)] = v;
          });
          setAssessmentStatus(res);
          setEventID(row.id);
          setStatusLoadingIdId(false);
        } catch (error) {
          setStatusLoadingIdId(false);
          dispatch(
            showSnackBar({
              setopen: true,
              message: error.message,
              severity: 'error',
            })
          );
        }
      }
      setShowStatusDialog(true);
    } else {
      setIsDialogOpen(true);
    }
  };

  const handleGiveAssessment = async (id) => {
    await getRequest(getCandidateAssessment(id))
      .then((res) => {
        setCurrentAssessmentDetails(res);
        setShowAssessmentDialog(true);
        setShowStatusDialog(false);
      })
      .catch((error) => {
        dispatch(
          showSnackBar({
            setopen: true,
            message: error.message,
            severity: 'error',
          })
        );
      });
  };

  const handleClickShowInterest = (row) => {
    setShowInterestDialog(true);
    setEventID(row.id);
    setEventName(row.event);
    resetShowInterest({
      comments: `I am interested in ${row.event}...`,
    });
  };

  const onSubmitShowInterest = () => {
    try {
      setLoading(true);
      const comments = watch('comments');
      putRequest(
        `${
          API_URL.SHOW_INTEREST
        }/${eventID}/showinterest?message=${encodeURIComponent(comments)}`
      )
        .then((res) => {
          dispatch(
            showSnackBar({
              setopen: true,
              message: res.msg,
              severity: 'success',
            })
          );
          setShowInterestDialog(false);
          resetShowInterest();
        })
        .catch((err) => {
          dispatch(
            showSnackBar({
              setopen: true,
              message: err.msg,
              severity: 'error',
            })
          );
        });
    } catch (error) {
      dispatch(
        showSnackBar({
          setopen: true,
          message: error?.msg,
          severity: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  function renderUpcomingEvents() {
    return (
      <UpcomingEventsSection
        upcomingEvents={upcomingEvents}
        role={role}
        handleClickRegister={handleClickRegister}
        handleClickShowInterest={handleClickShowInterest}
        statusLoadingIdId={statusLoadingIdId}
        handleDialogOpen={handleDialogOpen}
      />
    );
  }

  function renderPastEvents() {
    return (
      <TableContainer
        sx={{
          minHeight: 300,
          width: window.innerWidth < 400 ? 300 : '100%',
          overflowX: window.innerWidth < 400 ? 'hidden' : 'scroll',
        }}
        component={Paper}
      >
        <Table sx={{ minWidth: 650 }} stickyHeader aria-label="sticky table">
          <colgroup>
            <col
              style={{
                width: isMobileDevice ? '50%' : '20%',
              }}
            />
            <col style={{ width: isMobileDevice ? '25%' : '20%' }} />
            <col style={{ width: isMobileDevice ? '25%' : '20%' }} />
            <col style={{ width: isMobileDevice ? '25%' : '20%' }} />
            <col style={{ width: isMobileDevice ? '30%' : '20%' }} />
            {role === undefined ? <col style={{ width: '20%' }} /> : null}
          </colgroup>
          <TableHead>
            <TableRow>
              {isMobileDevice ? (
                <TableCell>{UI.EVENTS}</TableCell>
              ) : (
                <>
                  <TableCell>{UI.EVENTS}</TableCell>
                  <TableCell align="left">{UI.JOB_TITLE}</TableCell>
                  <TableCell align="left">{UI.COMPANY}</TableCell>
                  <TableCell align="left">{UI.START_DATE}</TableCell>
                  <TableCell align="left">{UI.END_DATE}</TableCell>
                  <TableCell align="left" />
                  <TableCell align="left" />
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {pastEvents?.map((row) => (
              <TableRow
                key={row.id}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                }}
              >
                {isMobileDevice ? (
                  <TableCell component="th" scope="row">
                    <a
                      onClick={() => handleDialogOpen(row.details)}
                      className={styles.eventNames}
                    >
                      <div>{row.event}</div>
                    </a>
                    <div className={styles.dateMobileView}>
                      <div>
                        <div>
                          <span>
                            <strong>{UI.JOB_TITLE}:</strong>&nbsp;
                          </span>
                          <span>
                            {row.jobTitle ? (
                              <a
                                href={`/job/${row.jobId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {row.jobTitle}
                              </a>
                            ) : (
                              <span style={{ paddingLeft: '30px' }}>-</span>
                            )}
                          </span>
                        </div>
                        <div>
                          <span>
                            <strong>{UI.COMPANY}:</strong>&nbsp;
                          </span>
                          <span>{row.companyName}</span>
                        </div>
                        <div>
                          <span>
                            <strong>{UI.START_DATE}:</strong>&nbsp;
                          </span>
                          <span>{getFormattedDateTimeWOSecs(row.start)}</span>
                        </div>
                        <div>
                          <span>
                            <strong>{UI.END_DATE}:</strong>&nbsp;
                          </span>
                          <span> {getFormattedDateTimeWOSecs(row.end)}</span>
                        </div>
                      </div>
                      {role !== LOGIN_TYPE.EMPLOYER && row.jobId && (
                        <Button
                          variant="contained"
                          style={{ width: '130px', whiteSpace: 'nowrap' }}
                          onClick={() =>
                            window.open(
                              `/job/${row.jobId}`,
                              '_blank',
                              'noopener,noreferrer'
                            )
                          }
                        >
                          Job Apply
                        </Button>
                      )}
                      {/* <Button
                          variant="contained"
                          onClick={() => handleOpenWinnersDetails(row.id)}
                        >
                          {UI.WINNERS}
                          {showWinnersDetails && row.id === eventID ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </Button> */}
                    </div>
                  </TableCell>
                ) : (
                  <>
                    <TableCell component="th" scope="row">
                      <a
                        onClick={() => handleDialogOpen(row.details)}
                        className={styles.eventNames}
                      >
                        {row.event}
                      </a>
                    </TableCell>
                    <TableCell align="left">
                      <span>
                        {row.jobTitle ? (
                          <a
                            href={`/job/${row.jobId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {row.jobTitle}
                          </a>
                        ) : (
                          <span style={{ paddingLeft: '30px' }}>-</span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell align="left">{row.companyName}</TableCell>
                    <TableCell align="left">
                      {getFormattedDateTimeWOSecs(row.start)}
                    </TableCell>
                    <TableCell align="left">
                      {getFormattedDateTimeWOSecs(row.end)}
                    </TableCell>
                    <TableCell>
                      {role !== LOGIN_TYPE.EMPLOYER && row.jobId && (
                        <Button
                          variant="contained"
                          fullWidth
                          style={{ width: '130px', whiteSpace: 'nowrap' }}
                          onClick={() =>
                            window.open(
                              `/job/${row.jobId}`,
                              '_blank',
                              'noopener,noreferrer'
                            )
                          }
                        >
                          Job Apply
                        </Button>
                      )}
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // 3) Helper to turn the enum key into a human string
  function formatStateLabel(rawState) {
    if (rawState === -1) return 'Not attempted yet';
    return getAssessmentState(rawState);
  }

  // 4) Helper to pick a Chip color
  function stateChipColor(rawState) {
    switch (rawState) {
      case ASSESSMENT_STATE.COMPLETED:
        return 'success';
      case ASSESSMENT_STATE.STARTED:
        return 'info';
      case ASSESSMENT_STATE.PENDING:
        return 'warning';
      case ASSESSMENT_STATE.NOT_TRIED_YET:
        return 'default';
      case ASSESSMENT_STATE.EXPIRED:
        return 'default';
      case ASSESSMENT_STATE.TERMINATED:
        return 'default';
      case ASSESSMENT_STATE.ERROR:
        return 'error';
      default:
        return 'default';
    }
  }

  function renderActionButton(
    rawState,
    rating,
    assessmentId,
    inWindow,
    onClick,
    type
  ) {
    let disabled = false;
    let variant;
    let handler;
    let label = '—';

    if (!inWindow) {
      disabled = true;
      label = UI.WAIT_FOR_EVENT_DATE;
    } else {
      switch (rawState) {
        case ASSESSMENT_STATE.COMPLETED:
          variant = 'contained';
          if (rating === -1) {
            disabled = true;
            label = 'Need Improvements';
          } else {
            label = 'View Report';
            handler = () =>
              window.open(
                `${URL.ASSESSMENT_REPORT}?id=${assessmentId}`,
                '_blank'
              );
          }
          break;

        case ASSESSMENT_STATE.EXPIRED:
        case ASSESSMENT_STATE.TERMINATED:
          disabled = true;
          label = '—';
          break;

        case ASSESSMENT_STATE.STARTED:
          disabled = true;
          label =
            type === 'assessment'
              ? UI.START_ASSESSMENT
              : UI.START_MOCK_INTERVIEW;
          break;

        case ASSESSMENT_STATE.PENDING:
          disabled = true;
          label = UI.WAIT_FOR_RESULT;
          break;

        case ASSESSMENT_STATE.NOT_TRIED_YET:
          variant = 'contained';
          label =
            type === 'assessment'
              ? UI.START_ASSESSMENT
              : UI.START_MOCK_INTERVIEW;
          handler = onClick;
          break;

        case ASSESSMENT_STATE.ERROR: {
          variant = 'contained';
          let errorIds;
          try {
            errorIds =
              JSON.parse(localStorage.getItem('ErrorAssessmentId')) || [];
          } catch {
            errorIds = [];
          }
          const hasErrored = errorIds.includes(String(assessmentId));
          if (hasErrored) {
            label = UI.START_AGAIN;
            handler = onClick;
          } else {
            label = UI.REPORT_ERROR;
            handler = () => {
              getRequest(
                `/event/${eventID}/report-error?assessmentid=${assessmentId}`
              ).then((res) => {
                errorIds.push(String(assessmentId));
                localStorage.setItem(
                  'ErrorAssessmentId',
                  JSON.stringify(errorIds)
                );
                dispatch(
                  showSnackBar({
                    setopen: true,
                    message: res?.msg,
                    severity: 'success',
                  })
                );
              });
            };
          }
          break;
        }

        default:
          disabled = true;
          label = '—';
      }
    }

    return (
      <Button
        style={{ width: '100%' }}
        size="small"
        variant={variant}
        disabled={disabled}
        onClick={handler}
      >
        {label}
      </Button>
    );
  }

  return (
    <div className={styles.container}>
      {/* UPCOMING EVENTS */}
      {/* eslint-disable-next-line react/jsx-no-bind */}
      <Accordion expanded={!showPastEvents} onChange={handlePastEvents}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h4" fontWeight={600}>
            {UI.UPCOMING_EVENTS}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {loading ? (
            <Box className="d-flex justify-content-center">
              <CircularProgress size="4rem" />
            </Box>
          ) : upcomingEvents?.length > 0 ? (
            renderUpcomingEvents()
          ) : (
            <div className={styles.noEvents}>
              <p>{UI.NO_UPCOMING_EVENTS}</p>
            </div>
          )}
        </AccordionDetails>
      </Accordion>

      {/* PAST EVENTS */}
      {/* eslint-disable-next-line react/jsx-no-bind */}
      <Accordion expanded={showPastEvents} onChange={handlePastEvents}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h4" fontWeight={600}>
            {UI.PAST_EVENTS}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {pastLoading ? (
            <Box className="d-flex justify-content-center">
              <CircularProgress size="4rem" />
            </Box>
          ) : pastEvents?.length > 0 ? (
            renderPastEvents()
          ) : (
            <div className={styles.noEvents}>
              <p>{UI.NO_PAST_EVENTS}</p>
            </div>
          )}
        </AccordionDetails>
      </Accordion>
      <TermAndConditionDialog
        termAndConditionDialogOpen={termAndConditionDialogOpen}
        onHandleDialogClose={handleDialogClose}
      >
        <iframe
          title={UI.PRICING_MODEL}
          src={`${eventDetailUrl}?embedded=true`}
          className={styles.pricing_modal}
        />
      </TermAndConditionDialog>
      <RegisterDialog
        isDialogOpen={isDialogOpen}
        onDialogClose={handleDialogClose}
        title={
          companyName === 'Global'
            ? 'Global Event Registration'
            : 'Event Registration'
        }
        content={
          <EventRegistrationForm
            companyName={companyName}
            eventName={eventName}
            eventID={selectedEventId}
            startDate={getFormattedDateTimeWOSecs(startDate)}
            endDate={getFormattedDateTimeWOSecs(endDate)}
            assessmentIds={assessmentIds}
            technologies={technologies}
            loading={loading}
            interviewSkillIds={interviewSkillIds}
            interviewSkills={interviewSkills}
            assessmentCount={assessmentCount}
            jobId={jobId}
          />
        }
        isAlert={false}
        isLoading={false}
      />

      {/* Show Interest Dialog for employers */}
      <Dialog
        open={showInterestDialog}
        onClose={() => {
          setShowInterestDialog(false);
        }}
        aria-labelledby="forms"
      >
        <DialogTitle
          sx={{
            minWidth: '400px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <strong>{UI.SHOW_INTEREST}</strong>
          <IconButton
            aria-label="close"
            onClick={() => {
              setShowInterestDialog(false);
            }}
            sx={COMMON_STYLE.DIALOG_HEADING}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '50px' }}>
          <div className={styles['form-content']}>
            <strong>{UI.EVENT_NAME}:</strong> {eventName}
          </div>
          <div className={styles['form-content']}>
            <strong>{UI.NAME}:</strong> {loginDetails?.name}
          </div>
          <div className={styles['form-content']}>
            <strong>{UI.EMAIL}:</strong> {loginDetails?.email}
          </div>
          <div className={styles['form-content']}>
            <strong>{UI.PHONE}:</strong> {loginDetails?.mobile}
          </div>
          <div>
            <strong>{UI.COMMENTS}</strong>
            <Box component="span" sx={{ color: '#d32f2f' }}>
              *
            </Box>
          </div>
          <TextField
            {...register('comments')}
            fullWidth
            size="small"
            rows={3}
            type="comments"
            id="comments"
            variant="outlined"
            defaultValue={`I am interested in ${eventName}...`}
            placeholder={UI.COMMENTS_PLACEHOLDER}
            name="comments"
            multiline
            error={!!showInterestErrors.comments?.message}
            helperText={
              showInterestErrors.comments?.message
                ? showInterestErrors.comments.message
                : ' '
            }
          />
          <div style={{ paddingBottom: '10px' }}>
            Or write to us at{' '}
            <a href="mailto:contact@alignmycareer.com">
              contact@alignmycareer.com
            </a>
          </div>
          <Button
            onClick={() => handleSubmitShowInterest(onSubmitShowInterest())}
            size="large"
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading}
            startIcon={
              loading && <CircularProgress size="1rem" color="inherit" />
            }
          >
            {UI.SUBMIT}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Status Dialog */}
      {/* Status Dialog */}
      <Dialog
        open={showStatusDialog}
        onClose={() => {
          setInterviewSkillIds(null);
          setInterviewSkills([]);
          setInterviewId(null);
          setShowStatusDialog(false);
        }}
        maxWidth="md"
      >
        <DialogContent sx={{ padding: '50px' }}>
          <IconButton
            aria-label="close"
            onClick={() => setShowStatusDialog(false)}
            sx={{
              position: 'absolute',
              right: 10,
              top: 10,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CancelOutlinedIcon />
          </IconButton>
          <TableContainer>
            {(() => {
              const inWindow = isUnderDateRange(startDate, endDate);

              return (
                <>
                  {/* ─── ASSESSMENT TABLE ─── */}
                  <Table>
                    <TableHead>
                      <TableRow sx={{ background: '#f5f5f5' }}>
                        <TableCell sx={{ width: '150px' }}>
                          <strong style={{ fontSize: 17 }}>
                            {UI.ASSESSMENTS}
                          </strong>
                        </TableCell>
                        <TableCell>
                          <strong style={{ fontSize: 17 }}>{UI.STATUS}</strong>
                        </TableCell>
                        <TableCell>
                          <strong style={{ fontSize: 17 }}>{UI.ACTIONS}</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {matchedTechnologies.map(({ value: id, label }) => {
                        const statusObj = assessmentStatus[id];
                        const rawState = statusObj?.state
                          ? Number(statusObj?.state)
                          : -1;
                        const rating = statusObj?.rating;
                        const assessmentId = statusObj?.id;
                        const chipLabel = formatStateLabel(rawState);
                        const chipColor = stateChipColor(rawState);

                        return (
                          <TableRow key={id}>
                            <TableCell>{label}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={inWindow ? chipLabel : 'Registered'}
                                color={inWindow ? chipColor : 'default'}
                                sx={{ width: '100%' }}
                              />
                            </TableCell>
                            <TableCell>
                              {renderActionButton(
                                rawState,
                                rating,
                                assessmentId,
                                inWindow,
                                () => handleGiveAssessment(id),
                                'assessment'
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  {/* ─── INTERVIEW TABLE ─── */}
                  {interviewId ? (
                    <Table sx={{ mt: 2 }}>
                      <TableHead>
                        <TableRow sx={{ background: '#f5f5f5' }}>
                          <TableCell sx={{ width: '150px' }}>
                            <strong style={{ fontSize: 17 }}>
                              {UI.INTERVIEWS}
                            </strong>
                          </TableCell>
                          <TableCell>
                            <strong style={{ fontSize: 17 }}>
                              {UI.STATUS}
                            </strong>
                          </TableCell>
                          <TableCell>
                            <strong style={{ fontSize: 17 }}>
                              {UI.ACTIONS}
                            </strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow key={interviewId}>
                          <TableCell>
                            <Stack spacing={0.5}>
                              {interviewSkills?.map((skill, idx) => (
                                <Typography key={idx} variant="body2">
                                  #{skill}
                                </Typography>
                              ))}
                            </Stack>
                          </TableCell>

                          {/* Status Chip */}
                          <TableCell>
                            {(() => {
                              const statusObj = assessmentStatus[interviewId];
                              const rawState = statusObj?.state
                                ? Number(statusObj?.state)
                                : -1;
                              const chipLabel = formatStateLabel(rawState);
                              const chipColor = stateChipColor(rawState);
                              return (
                                <Chip
                                  size="small"
                                  label={inWindow ? chipLabel : 'Registered'}
                                  color={inWindow ? chipColor : 'default'}
                                  sx={{ width: '100%' }}
                                />
                              );
                            })()}
                          </TableCell>

                          {/* Action Button */}
                          <TableCell>
                            {(() => {
                              const statusObj = assessmentStatus[interviewId];
                              const rawState = statusObj?.state
                                ? Number(statusObj?.state)
                                : -1;
                              const assessmentId = statusObj?.id;
                              const rating = statusObj?.rating;
                              return renderActionButton(
                                rawState,
                                rating,
                                assessmentId,
                                inWindow,
                                () => handleGiveAssessment(interviewId),
                                'interview'
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  ) : null}
                </>
              );
            })()}
          </TableContainer>
        </DialogContent>
      </Dialog>
      {/* Assessment Dialog */}
      <Dialog
        open={showAssessmentDialog}
        onClose={() => setShowAssessmentDialog(false)}
        sx={{ overflow: 'visible' }}
        PaperProps={{
          sx: {
            minWidth: 500,
          },
        }}
      >
        <DialogContent dividers component="div" style={{ padding: 18 }}>
          <IconButton
            aria-label="close"
            onClick={() => setShowAssessmentDialog(false)}
            sx={{
              position: 'absolute',
              right: 18,
              top: 20,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CancelOutlinedIcon />
          </IconButton>
          <AssessmentDetailsDialog
            shouldShowExtraItems={showExtraItems()}
            assessmentDetails={currentAssessmentDetails}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
export default Events;
Events.propTypes = {
  role: PropTypes.number,
  setShowNavBar: PropTypes.func,
};
Events.defaultProps = {
  role: undefined,
  setShowNavBar: noop,
};
